import React, { useState, useEffect, useRef, useCallback } from "react";
import useGameBase from "../../hooks/useGameBase";
import SocketContext from "../../context/SocketContext";
import RebusCardSvg from "./RebusCardSvg";

export default function RebusGame({
  roomId,
  gameState,
  gamePlayers = [],
  user = {},
}) {
  const socket = React.useContext(SocketContext);
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const socketId = socket?.id || user?.socketId;

  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null); // null | "correct" | "incorrect"
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);
  const [countdownSecs, setCountdownSecs] = useState(null);
  const [nextRoundSecs, setNextRoundSecs] = useState(null);
  const [roundTimeSecs, setRoundTimeSecs] = useState(null);

  const inputRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const nextRoundIntervalRef = useRef(null);
  const roundTimerIntervalRef = useRef(null);
  const prevRoundRef = useRef(null);

  const phase = gameState?.phase || "waiting";
  const puzzle = gameState?.puzzle || null;
  const answeredPlayers = gameState?.answeredPlayers || [];
  const round = gameState?.round || 0;
  const rounds = gameState?.rounds || 10;
  const countdownAfterFirst = gameState?.countdownAfterFirst || 20;
  const countdownStartAt = gameState?.countdownStartAt || null;
  const nextRoundAt = gameState?.nextRoundAt || null;
  const roundStartAt = gameState?.roundStartAt || null;
  const timeLimit = gameState?.timeLimit || 60;
  const results = gameState?.results || [];
  const finished = !!gameState?.finished;
  const result = gameState?.result || null;
  const players = Array.isArray(gameState?.players) ? gameState.players : gamePlayers;

  // Reset local state when round changes
  useEffect(() => {
    if (prevRoundRef.current !== round) {
      setAnswer("");
      setFeedback(null);
      setAlreadyAnswered(false);
      prevRoundRef.current = round;
    }
  }, [round]);

  // Sync alreadyAnswered when answeredPlayers changes
  useEffect(() => {
    if (answeredPlayers.includes(socketId)) {
      setAlreadyAnswered(true);
    }
  }, [answeredPlayers, socketId]);

  // Focus input when active
  useEffect(() => {
    if ((phase === "playing" || phase === "countdown") && !alreadyAnswered) {
      inputRef.current?.focus();
    }
  }, [phase, alreadyAnswered]);

  // Main round countdown timer
  useEffect(() => {
    if (roundTimerIntervalRef.current) {
      clearInterval(roundTimerIntervalRef.current);
      roundTimerIntervalRef.current = null;
    }
    if (phase === "playing" && roundStartAt) {
      const tick = () => {
        const elapsed = Math.floor((Date.now() - roundStartAt) / 1000);
        const remaining = Math.max(0, timeLimit - elapsed);
        setRoundTimeSecs(remaining);
        if (remaining <= 0) {
          clearInterval(roundTimerIntervalRef.current);
          roundTimerIntervalRef.current = null;
        }
      };
      tick();
      roundTimerIntervalRef.current = setInterval(tick, 250);
    } else {
      setRoundTimeSecs(null);
    }
    return () => {
      if (roundTimerIntervalRef.current) clearInterval(roundTimerIntervalRef.current);
    };
  }, [phase, roundStartAt, timeLimit]);

  // After-first-correct countdown timer
  useEffect(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (phase === "countdown" && countdownStartAt) {
      const tick = () => {
        const elapsed = Date.now() - countdownStartAt;
        const remaining = Math.max(0, countdownAfterFirst - Math.floor(elapsed / 1000));
        setCountdownSecs(remaining);
        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      };
      tick();
      countdownIntervalRef.current = setInterval(tick, 250);
    } else {
      setCountdownSecs(null);
    }
    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [phase, countdownStartAt, countdownAfterFirst]);

  // Next-round countdown timer
  useEffect(() => {
    if (nextRoundIntervalRef.current) {
      clearInterval(nextRoundIntervalRef.current);
      nextRoundIntervalRef.current = null;
    }
    if (nextRoundAt) {
      const tick = () => {
        const remaining = Math.max(0, Math.ceil((nextRoundAt - Date.now()) / 1000));
        setNextRoundSecs(remaining);
        if (remaining <= 0) {
          clearInterval(nextRoundIntervalRef.current);
          nextRoundIntervalRef.current = null;
        }
      };
      tick();
      nextRoundIntervalRef.current = setInterval(tick, 250);
    } else {
      setNextRoundSecs(null);
    }
    return () => {
      if (nextRoundIntervalRef.current) clearInterval(nextRoundIntervalRef.current);
    };
  }, [nextRoundAt]);

  const handleSubmit = useCallback(async () => {
    const trimmed = answer.trim();
    if (!trimmed || alreadyAnswered) return;
    try {
      const ack = await submitAction({ type: "submitAnswer", answer: trimmed });
      if (ack && ack.correct) {
        setFeedback("correct");
        setAlreadyAnswered(true);
        setAnswer("");
      } else {
        setFeedback("incorrect");
        setAnswer("");
        setTimeout(() => setFeedback(null), 1200);
      }
    } catch (e) {
      setFeedback("incorrect");
      setAnswer("");
      setTimeout(() => setFeedback(null), 1200);
    }
  }, [answer, alreadyAnswered, submitAction]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  const fmtTime = (ms) => {
    if (ms == null) return "–";
    return (ms / 1000).toFixed(1) + "s";
  };

  const latestResult = results.length > 0 ? results[results.length - 1] : null;
  const renderPuzzleVisual = (currentPuzzle, maxHeightClass) => {
    if (!currentPuzzle) return null;

    if (currentPuzzle.card) {
      return (
        <RebusCardSvg
          card={currentPuzzle.card}
          title="Rebus puzzle"
          className={`w-full ${maxHeightClass}`}
          darkMode={user?.darkMode}
        />
      );
    }

    if (currentPuzzle.imageUrl) {
      return (
        <img
          src={currentPuzzle.imageUrl}
          alt="Rebus puzzle"
          className={`max-w-full ${maxHeightClass} object-contain rounded-lg select-none`}
          draggable={false}
        />
      );
    }

    return null;
  };

  if (!gameState) return <div className="p-4 text-gray-900 dark:text-gray-100">Loading…</div>;

  return (
    <div className="p-4 w-full max-w-xl mx-auto text-gray-900 dark:text-gray-100">

      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Round {round}/{rounds}
        </span>
      </div>

      {/* ── Puzzle card ── */}
      {(phase === "playing" || phase === "countdown") && puzzle && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
              What word or phrase does this represent?
            </p>
            {phase === "playing" && roundTimeSecs != null && (
              <span className={`text-sm font-bold font-mono px-3 py-1 rounded-full ${
                roundTimeSecs <= 10
                  ? "bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 animate-pulse"
                  : roundTimeSecs <= 20
                  ? "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
              }`}>
                {roundTimeSecs}s
              </span>
            )}
          </div>

          {/* Puzzle image */}
          <div className="flex items-center justify-center mb-5 border rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
            {renderPuzzleVisual(puzzle, "max-h-64")}
          </div>

          {/* Auto-revealed hint */}
          {puzzle.hint && puzzle.hintVisible && (
            <div className="text-center mb-4">
              <p className="text-sm italic text-gray-500 dark:text-gray-400">
                <span className="mr-1 text-amber-500 dark:text-amber-400 not-italic font-semibold">Hint:</span>
                💡 {puzzle.hint}
              </p>
            </div>
          )}

          {/* Countdown banner */}
          {phase === "countdown" && (
            <div className="mb-4 text-center">
              {alreadyAnswered ? (
                <p className="text-green-600 dark:text-green-400 font-semibold text-sm">
                  ✓ Correct! Waiting for others… ({countdownSecs ?? ""}s)
                </p>
              ) : (
                <p className="text-amber-600 dark:text-amber-400 font-semibold text-sm animate-pulse">
                  Someone got it! You have {countdownSecs ?? ""}s left
                </p>
              )}
            </div>
          )}

          {/* Answer input */}
          {!alreadyAnswered && (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">
                Your answer:
              </label>
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <input
                  ref={inputRef}
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type the word or phrase…"
                  autoComplete="off"
                  className={`w-full sm:flex-1 p-2 rounded-lg border text-lg font-medium min-w-0
                    dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500
                    ${feedback === "incorrect"
                      ? "border-red-400 bg-red-50 dark:bg-red-900/30"
                      : "border-gray-300 bg-white"
                    }`}
                />
                <button
                  onClick={handleSubmit}
                  className="w-full sm:w-auto px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-colors whitespace-nowrap"
                  style={{ minWidth: "4.5rem" }}
                >
                  Go!
                </button>
              </div>
              {feedback === "incorrect" && (
                <p className="mt-1 text-sm text-red-500 dark:text-red-400">
                  ✗ Not quite — try again!
                </p>
              )}
            </div>
          )}

          {alreadyAnswered && (
            <div className="mt-4 text-center text-green-600 dark:text-green-400 font-bold text-lg">
              ✓ Correct!
            </div>
          )}
        </div>
      )}

      {/* ── Round results ── */}
      {phase === "results" && latestResult && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
          <h2 className="text-lg font-bold mb-1 text-center">
            Round {latestResult.round} Results
          </h2>

          {/* Show the answer */}
          <div className="mb-4 text-center">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Answer</span>
            <div className="text-3xl font-extrabold text-purple-600 dark:text-purple-400 mt-1">
              {latestResult.answer}
            </div>
          </div>

          {/* Re-show the puzzle image small */}
          {latestResult.puzzle && (latestResult.puzzle.imageUrl || latestResult.puzzle.card) && (
            <div className="flex items-center justify-center mb-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              {renderPuzzleVisual(latestResult.puzzle, "max-h-36")}
            </div>
          )}

          {/* Per-player times */}
          <div className="space-y-2">
            {(latestResult.perPlayer || [])
              .slice()
              .sort((a, b) => {
                if (a.dnf && !b.dnf) return 1;
                if (!a.dnf && b.dnf) return -1;
                return (a.timeMs || 9e9) - (b.timeMs || 9e9);
              })
              .map((p, i) => {
                const isMe = p.socketId === socketId;
                return (
                  <div
                    key={p.socketId}
                    className={`flex items-center justify-between rounded-lg px-4 py-2
                      ${isMe
                        ? "bg-purple-50 dark:bg-purple-900/40 font-semibold"
                        : "bg-gray-50 dark:bg-gray-700"
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      {!p.dnf ? (
                        <span className="text-xs w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
                          {i + 1}
                        </span>
                      ) : (
                        <span className="text-xs w-5 h-5 rounded-full bg-gray-400 text-white flex items-center justify-center">
                          –
                        </span>
                      )}
                      <span>{p.username}{isMe ? " (you)" : ""}</span>
                    </div>
                    <span className={`text-sm font-mono font-bold ${p.dnf ? "text-gray-400 dark:text-gray-500" : "text-green-600 dark:text-green-400"}`}>
                      {p.dnf ? "Did Not Finish" : fmtTime(p.timeMs)}
                    </span>
                  </div>
                );
              })}
          </div>

          {/* Next round countdown */}
          {round < rounds && nextRoundSecs != null && (
            <div className="mt-4 text-center">
              <span className="inline-block px-4 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-sm font-semibold">
                Next round in {nextRoundSecs}s
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Game finished ── */}
      {finished && result && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4 text-center">
          <h2 className="text-2xl font-bold mb-2">
            {result.winners && result.winners.includes(socketId)
              ? "🎉 You Won!"
              : result.isTie
              ? "🤝 It's a tie!"
              : `Winner: ${
                  players.find(
                    (p) => result.winners && result.winners.includes(p.socketId)
                  )?.username || "Unknown"
                }`}
          </h2>
          <div className="mt-4 space-y-2">
            {(result.breakdown || []).map((p, i) => (
              <div
                key={p.socketId}
                className={`flex items-center justify-between rounded-lg px-4 py-2 ${
                  p.socketId === socketId
                    ? "bg-purple-50 dark:bg-purple-900/40 font-semibold"
                    : "bg-gray-50 dark:bg-gray-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold">
                    {i + 1}
                  </span>
                  <span>
                    {p.username}
                    {p.socketId === socketId ? " (you)" : ""}
                  </span>
                </div>
                <span className="font-mono font-bold">{p.score} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Waiting state */}
      {phase === "waiting" && !finished && (
        <div className="text-center py-10 text-gray-400 dark:text-gray-500">
          <p className="text-3xl mb-2">🧩</p>
          <p>Game starting…</p>
        </div>
      )}
    </div>
  );
}

RebusGame.Guide = function RebusGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Rebus</h3>
      <ul className="list-disc ml-5 space-y-1 text-sm">
        <li>Each round shows a picture puzzle — work out the word or phrase it represents.</li>
        <li>Type your answer and press <strong>Go!</strong> as fast as you can.</li>
        <li>First correct answer scores <strong>10 points</strong>.</li>
        <li>Subsequent correct answers score <strong>5 points</strong>.</li>
        <li>Once someone gets it right, a short countdown gives others extra time.</li>
        <li>Stuck? Hit <em>Show hint</em> for a clue — but it might cost you time!</li>
      </ul>
    </div>
  );
};
